import { LayoutGrid, List } from "lucide-react";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";

export type ViewMode = "cards" | "list";

/**
 * Estado de modo de visualizacao (cartoes/lista) persistido em
 * ``localStorage`` por ``storageKey``. Reutilizavel em qualquer listagem
 * (KBs, times, agentes...).
 */
export function useViewMode(
  storageKey: string,
  fallback: ViewMode = "cards",
): [ViewMode, (mode: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(() => {
    const stored = window.localStorage.getItem(storageKey);
    return stored === "cards" || stored === "list" ? stored : fallback;
  });
  const set = useCallback(
    (next: ViewMode) => {
      setMode(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // localStorage indisponivel (modo privado): mantem so em memoria.
      }
    },
    [storageKey],
  );
  return [mode, set];
}

interface ViewToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  labels?: { cards?: string; list?: string };
  className?: string;
}

export function ViewToggle({
  value,
  onChange,
  labels,
  className,
}: ViewToggleProps) {
  const cardsLabel = labels?.cards ?? "Cartões";
  const listLabel = labels?.list ?? "Lista";
  const btn = (active: boolean) =>
    cn(
      "flex h-8 w-9 items-center justify-center rounded-md transition-colors",
      active
        ? "bg-canvas text-ink-950"
        : "text-ink-500 hover:text-ink-900",
    );
  return (
    <div
      className={cn(
        "inline-flex rounded-lg border border-line bg-surface p-0.5",
        className,
      )}
      role="group"
    >
      <button
        type="button"
        onClick={() => onChange("cards")}
        aria-pressed={value === "cards"}
        aria-label={cardsLabel}
        title={cardsLabel}
        className={btn(value === "cards")}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange("list")}
        aria-pressed={value === "list"}
        aria-label={listLabel}
        title={listLabel}
        className={btn(value === "list")}
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  );
}
